/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useState, useEffect } from "react";
import Input from "components/generals/Input";
import Typography from "components/generals/Typography";
import Box from "components/generals/Box";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import useHttp from "hooks/http.hook";
import { UserInterface } from "@malmur-technologies/medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import PasswordRecoveryCard from "components/generals/PasswordRecoveryCard";
import ErrorIcon from "@mui/icons-material/Error";

/**
 * Properties for the VerifyCode component.
 * 
 * @interface VerifyCodeProps
 * @property {Function} onNext - Callback when code is verified
 * @property {UserInterface | null} user - User account being recovered
 */
interface VerifyCodeProps {
  onNext: () => void;
  user: UserInterface | null;
}

/**
 * Verify security code component for password recovery.
 * Second step validating user identity via emailed code.
 * 
 * Features:
 * - 6-digit security code input
 * - Code validation
 * - Resend code functionality
 * - Countdown timer (3 minutes)
 * - Code expiry handling
 * - Error messaging
 * - Loading state
 * - Email display
 * - i18n translation support
 * - Progress to password change step
 * 
 * Verification Flow:
 * 1. User receives code via email
 * 2. Enters 6-digit code
 * 3. Submits for verification
 * 4. API validates code
 * 5. Advances to password change on success
 * 
 * Timer Management:
 * - 180 second (3 minute) countdown
 * - Enables resend when expired
 * - Automatic countdown display
 * - Interval cleanup on unmount
 * 
 * Resend Functionality:
 * - Requests new code from API
 * - Resets countdown timer
 * - Sends to same email address
 * - Error handling for failures
 * 
 * Error Handling:
 * - Required field validation
 * - Invalid code message
 * - API error handling
 * - User-friendly error display
 * 
 * @param {VerifyCodeProps} props - Component properties
 * @returns {JSX.Element} Security code verification form
 */
const VerifyCode = ({ onNext, user }: VerifyCodeProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, , sendRequest] = useHttp();
  const [resendTimer, setResendTimer] = useState(180);

  const email = user?.email;

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const resendCode = () => {
    sendRequest(
      CoreAPI_URLs.PASSWORD_RECOVERY_SEARCH,
      null,
      "POST",
      JSON.stringify({ email }),
      null,
      { applyErrorFn: (_message) => setError(t("passwordRecovery.userNotFound")) }
    );
    setResendTimer(180);
  };

  const handleVerify = async () => {
    setError("");
    if (!code) return setError(t("passwordRecovery.requiredFields"));

    sendRequest(
      CoreAPI_URLs.PASSWORD_RECOVERY_SECURITY_CODE,
      (result: boolean) => {
        result ? onNext() : setError(t("passwordRecovery.invalidCode"));
      },
      "POST",
      JSON.stringify({ username: user?.username, code }),
      null,
      { applyErrorFn: (_message) => setError(t("passwordRecovery.invalidCode")) }
    );
  };

  return (
    <PasswordRecoveryCard
      title={t("passwordRecovery.securityCodeTitle")}
      description={t("passwordRecovery.securityCodeDescription")}
      footer={
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Button variant="contained" color="info" onClick={handleVerify} disabled={isLoading}>
            {t("passwordRecovery.continue")}
          </Button>
        </Box>
      }
      timer={
        <Button
          variant="outlined"
          fullWidth
          onClick={resendCode}
          disabled={resendTimer > 0}
          sx={{ fontSize: 9 }}
        >
          {resendTimer > 0
            ? `${t("login.resendAvailableIn")} ${Math.floor(resendTimer / 60)}:${String(
                resendTimer % 60
              ).padStart(2, "0")}`
            : t("login.resendCode")}
        </Button>
      }
    >
      <Input
        label={t("passwordRecovery.enterCode")}
        variant="outlined"
        fullWidth
        value={code}
        onChange={(e) => setCode(e.target.value)}
        sx={{ mt: 3 }}
      />
      {error && (
        <Box mt={2}>
          <Typography
            variant="caption"
            sx={{ color: "#f44336", display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <ErrorIcon fontSize="small" />
            {error}
          </Typography>
        </Box>
      )}
      <Typography variant="caption" fontSize={13} marginBottom={2} sx={{ mt: 2, display: "block" }}>
        <strong>{t("passwordRecovery.emailSend")}</strong> {user?.email}
      </Typography>
    </PasswordRecoveryCard>
  );
};

export default VerifyCode;
